/*
 * @Author: changdou.fe@bytedance.com
 * @Date: 2025-10-18 18:14:52
 * @LastEditors: changdou.fe@bytedance.com
 * @LastEditTime: 2025-10-25 13:05:24
 */
'use client';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Tooltip } from 'antd';
import InviteModal from '@/components/invite-modal';
import RewardModal from '@/components/reward-modal';
import { useInviteModal } from '@/hooks/use-invite-modal';
import { useRewardModal } from '@/hooks/use-reward-modal';
import { MessageCircleMore, MessageCircleQuestionMark } from 'lucide-react';
import { InviteIcon, RewardPlanIcon } from '@/app/const/svg';
import CoLivePopover from '@/components/co-live-popover';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useSupport } from '@/hooks/use-support';
import { useUserInfo } from '@/app/stores/useUserStore';
import { captureReferralShareClick } from '@/lib/analytics/posthog-events';
import { LoginTips } from '@/components/login/tips';
import streamToolsEntryIcon from '@/app/assets/image/live/logo.svg';

export function MainFooter() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // 用户信息
  const userInfo = useUserInfo();
  // 奖励计划弹窗逻辑
  const {
    isOpen: isRewardOpen,
    openModal: openRewardModal,
    closeModal: closeRewardModal,
    shouldAutoShow: rewardShouldAutoShow,
    loading: rewardLoading,
    rewardData,
  } = useRewardModal();
  // 邀请弹窗逻辑 - 奖励计划弹窗优先时或加载中时禁用自动弹出
  const { isOpen, openModal, closeModal } = useInviteModal({ disabled: rewardLoading || rewardShouldAutoShow });
  // 获取支持链接（必须在组件顶层调用 Hook）
  const supportQAUrl = useSupport('qA');
  // 如果没有登录的话，则不显示邀请按钮
  if (!userInfo?.userId) {
    return (
      <footer className="hidden md:block fixed bottom-0 left-0 right-0 bg-background border-t border-button-border/50 z-[50] shadow-md-t">
        <LoginTips />
      </footer>
    );
  }
  return (
    <footer className="hidden md:block fixed bottom-0 left-0 right-0 bg-background border-t border-button-border/50 z-[50] shadow-md-t">
      <div className="flex items-center justify-between px-4 h-[40px] ">
        <div className="flex items-center gap-2">
          {/* <div className="co-logo co-logo-sm" style={{ marginTop: '-5px' }}></div> */}
          {/* <span className="text-xs font-normal text-font-secondary">{t('footer.description')}</span> */}
        </div>
        <div className="flex items-center gap-4">
          <Tooltip title={t('footer.followUsOnTwitter')}>
            <Link
              className="w-4 h-4 co-link co-twitter-icon"
              to="https://x.com/Billion_Global"
              target="_blank"
              onClick={() => captureReferralShareClick({ source: 'main_footer', platform: 'x' })}
            />
          </Tooltip>
          <Tooltip title={t('footer.joinOurDiscord')}>
            <Link
              className="w-4 h-4 co-link co-discord-icon"
              to="https://discord.com/invite/Csj669ra3U"
              target="_blank"
              onClick={() => captureReferralShareClick({ source: 'main_footer', platform: 'discord' })}
            />
          </Tooltip>
          <Tooltip title={t('footer.joinOurTelegram')}>
            <Link
              className="w-4 h-4 co-link co-telegram-icon"
              to="https://t.me/billionlive_global"
              target="_blank"
              onClick={() => captureReferralShareClick({ source: 'main_footer', platform: 'telegram' })}
            />
          </Tooltip>
          <div className="w-px h-5 bg-button-border mx-1" />
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              size="smm"
              className="h-6 px-2 text-font-primary/70"
              onClick={() => {
                window.open(supportQAUrl, '_blank');
              }}
            >
              <MessageCircleQuestionMark className="!size-3.5 mr-0.5 flex-none" />
              {t('footer.support')}
            </Button>
            <Button
              variant="secondary"
              size="smm"
              className="h-6 px-2 text-font-primary/70"
              onClick={() => {
                navigate('/feedback');
              }}
            >
              <MessageCircleMore className="!size-3.5 mr-0.5 flex-none" />
              {t('help.feedback')}
            </Button>
            {
              !process.env.isProd && (
                <Button
                  variant="default-outline"
                  size="smm"
                  className="h-6 px-1.5"
                  onClick={() => {
                    navigate('/stream/tools');
                  }}
                >
                  <img
                    src={streamToolsEntryIcon}
                    alt="stream-tools"
                    className="size-4 mr-0.5 flex-none"
                  />
                  直播助手
                </Button>
              )
            }
            <Button
              variant="default-outline"
              size="smm"
              className="h-6 px-1.5"
              onClick={() => {
                if (isOpen) {
                  closeModal();
                } else {
                  if (isRewardOpen) closeRewardModal();
                  openModal();
                }
              }}
            >
              <InviteIcon className="w-3 h-3 mr-0.5" />
              {t('header.invite')}
            </Button>
            {/* 奖励计划入口 */}
            {rewardData?.active && (
              <Button
                variant="default-outline"
                size="smm"
                className="h-6 px-1.5"
                onClick={() => {
                  if (isRewardOpen) {
                    closeRewardModal();
                  } else {
                    if (isOpen) closeModal();
                    openRewardModal();
                  }
                }}
              >
                <RewardPlanIcon className="!size-3 mr-0.5 flex-none" />
                {t('rewardPlan.entry')}
              </Button>
            )}
          </div>
        </div>
      </div>
      {/* 当前观看直播 */}
      <CoLivePopover />
      {/* 邀请弹窗 */}
      <InviteModal
        isOpen={isOpen}
        onClose={closeModal}
        onInviteClick={() => {
          console.log('邀请好友按钮被点击');
        }}
      />
      {/* 奖励计划弹窗 */}
      <RewardModal
        isOpen={isRewardOpen}
        onClose={closeRewardModal}
        amount={rewardData?.amount}
        endTime={rewardData?.endTime}
        active={rewardData?.active}
      />
    </footer>
  );
}
